/**
 * `/admin/env` — env diagnostics admin page (#266).
 *
 * Renders a grouped table of every entry in the server-side
 * `ENV_SCHEMA` with status chips, masked values, and per-row Reveal /
 * Copy affordances for secrets.
 *
 * Layout:
 *   - Heading row + descriptive lead.
 *   - Summary `<Alert>` banner — severity flips:
 *       error   → any required var is missing or placeholder
 *                 (broken deploy, surface in red).
 *       warning → at least one optional var holds a placeholder value
 *                 (deploy works but operator left a sentinel).
 *       success → all rows are ok / using-default.
 *   - A single MUI Table grouped by `group`. Group headers are
 *     non-clickable typography rows that visually break the table
 *     while keeping it semantically one table (so screen readers can
 *     still arrow through columns continuously).
 *
 * Reveal UX (security):
 *   - Per-row state holds `{ value, expiresAt }` while a secret is
 *     unmasked. A `useEffect` sets a `setTimeout` keyed on `expiresAt`
 *     to re-mask after 10 seconds. The timer is cleared on unmount /
 *     re-reveal to avoid stale closures racing with a fresh value.
 *   - Each reveal hits the server, which audits the call. There is no
 *     client-side debounce — every press is an auditable event.
 *
 * Client-fetched only (issue #266 "no SSR leak"):
 *   The page calls TanStack Query directly; the API is the only thing
 *   that ever sees the actual values, and the HTML shipped to the
 *   browser pre-auth is the empty admin SPA shell. Verified by
 *   inspecting the page source while logged out — should be empty.
 */

import { useEffect, useMemo, useRef, useState } from "react";
import type { ReactElement } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { useEnvDiagnostics, useRevealEnvVar, type EnvVarWire } from "../hooks/useEnvDiagnostics";

/** How long a revealed secret stays in plaintext on the page. */
const REVEAL_TTL_MS = 10_000;

/**
 * Human-facing group labels. Server emits the canonical token; the UI
 * substitutes the display string. Keep these short — they render as
 * full-width section dividers in the table.
 */
const GROUP_LABELS: Record<EnvVarWire["group"], string> = {
  runtime: "Runtime",
  database: "Database",
  auth: "Authentication",
  public: "Public",
  ai: "AI provider",
  image: "Image provider",
  analytics: "Analytics",
  cookies: "Cookies",
  geoip: "GeoIP",
};

type ChipColor = "default" | "primary" | "secondary" | "error" | "info" | "success" | "warning";

/**
 * Map a status token to an MUI Chip color. The palette mirrors the
 * "highlight semantics" section of the issue spec:
 *   missing      → error
 *   placeholder  → warning
 *   using-default → info
 *   ok           → success
 */
function chipColorFor(status: EnvVarWire["status"]): ChipColor {
  switch (status) {
    case "missing":
      return "error";
    case "placeholder":
      return "warning";
    case "using-default":
      return "info";
    case "ok":
    default:
      return "success";
  }
}

function statusLabelFor(status: EnvVarWire["status"]): string {
  switch (status) {
    case "missing":
      return "Missing";
    case "placeholder":
      return "Placeholder";
    case "using-default":
      return "Using default";
    case "ok":
    default:
      return "OK";
  }
}

/**
 * Summary banner severity + message derived from the list. The order
 * matches the issue spec: any broken-required wins, then any
 * placeholder, then success.
 */
function summaryFor(rows: EnvVarWire[] | undefined): {
  severity: "error" | "warning" | "success";
  message: string;
} {
  if (!rows || rows.length === 0) {
    return { severity: "success", message: "No env vars to report." };
  }
  const brokenRequired = rows.filter(
    (r) => r.required && (r.status === "missing" || r.status === "placeholder"),
  );
  if (brokenRequired.length > 0) {
    const noun = brokenRequired.length === 1 ? "var is" : "vars are";
    return {
      severity: "error",
      message: `${brokenRequired.length} required env ${noun} missing or placeholder — deploy is broken.`,
    };
  }
  const placeholderOptional = rows.filter((r) => !r.required && r.status === "placeholder");
  if (placeholderOptional.length > 0) {
    const noun = placeholderOptional.length === 1 ? "var is" : "vars are";
    return {
      severity: "warning",
      message: `${placeholderOptional.length} optional env ${noun} set to a placeholder value.`,
    };
  }
  return { severity: "success", message: "All env vars look good." };
}

/**
 * Group rows by `group` while preserving the server's order. The server
 * sorts by `ENV_GROUP_ORDER` and declaration order within a group, so
 * we just walk the array and bucket by group as we see it.
 */
function groupRows(rows: EnvVarWire[]): Array<{ group: EnvVarWire["group"]; rows: EnvVarWire[] }> {
  const out: Array<{ group: EnvVarWire["group"]; rows: EnvVarWire[] }> = [];
  for (const row of rows) {
    const last = out[out.length - 1];
    if (last && last.group === row.group) {
      last.rows.push(row);
    } else {
      out.push({ group: row.group, rows: [row] });
    }
  }
  return out;
}

type RevealedEntry = { value: string; expiresAt: number };

export function EnvDiagnosticsPage(): ReactElement {
  const { data: rows, isPending, isError, error } = useEnvDiagnostics();
  const reveal = useRevealEnvVar();

  /**
   * Per-row plaintext state. Keyed by env-var name. Cleared by the
   * effect below when each entry's `expiresAt` ticks past `Date.now()`.
   */
  const [revealed, setRevealed] = useState<Record<string, RevealedEntry>>({});
  const [toast, setToast] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "info" | "error";
  }>({ open: false, message: "", severity: "info" });

  /**
   * Holds active setTimeout ids per name so a re-reveal cancels the
   * outstanding timer before installing a new one. Avoids the race
   * where two timeouts both clear the same value and the second
   * accidentally cancels a fresh reveal mid-flight.
   */
  const timersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  // Re-mask each revealed entry exactly once, at its expiresAt. The
  // setTimeout is scoped to the entry's lifetime and torn down on
  // unmount + on re-reveal.
  useEffect(() => {
    const timers = timersRef.current;
    for (const [name, entry] of Object.entries(revealed)) {
      if (timers.has(name)) continue;
      const ms = Math.max(0, entry.expiresAt - Date.now());
      const id = setTimeout(() => {
        setRevealed((cur) => {
          const next = { ...cur };
          delete next[name];
          return next;
        });
        timers.delete(name);
      }, ms);
      timers.set(name, id);
    }
    return () => {
      // Component-level cleanup: nothing on every render (per-name
      // teardown happens above), but the outer-effect return clears
      // ALL timers when the page unmounts. This avoids the SetState-
      // after-unmount warning if the operator navigates away while a
      // reveal is still active.
    };
  }, [revealed]);

  useEffect(() => {
    // Capture the ref's current value at effect-setup time. The cleanup
    // closes over THIS Map specifically; if a later render swaps the
    // ref's target the closure still tears down the one this effect
    // installed timers on. (React hooks/exhaustive-deps lint flagged
    // the unswapped read.)
    const timers = timersRef.current;
    return () => {
      // Final cleanup on unmount — clear every outstanding timer.
      for (const id of timers.values()) clearTimeout(id);
      timers.clear();
    };
  }, []);

  const grouped = useMemo(() => (rows ? groupRows(rows) : []), [rows]);
  const summary = useMemo(() => summaryFor(rows), [rows]);

  function handleReveal(name: string) {
    reveal.mutate(
      { name },
      {
        onSuccess: (result) => {
          // Cancel any in-flight timer for this name before installing
          // a fresh one. Otherwise a quick double-reveal would re-mask
          // the second value on the FIRST timer's schedule.
          const existing = timersRef.current.get(name);
          if (existing) {
            clearTimeout(existing);
            timersRef.current.delete(name);
          }
          setRevealed((cur) => ({
            ...cur,
            [name]: { value: result.value, expiresAt: Date.now() + REVEAL_TTL_MS },
          }));
          setToast({
            open: true,
            message: `${name} revealed. Auto-hides in 10 seconds. The reveal was logged.`,
            severity: "info",
          });
        },
        onError: (err) => {
          setToast({
            open: true,
            message: err.message ?? "Reveal failed.",
            severity: "error",
          });
        },
      },
    );
  }

  function handleCopy(name: string, value: string) {
    void navigator.clipboard
      .writeText(value)
      .then(() => {
        setToast({ open: true, message: `Copied ${name} to clipboard.`, severity: "success" });
      })
      .catch(() => {
        setToast({
          open: true,
          message: "Copy failed — clipboard unavailable.",
          severity: "error",
        });
      });
  }

  return (
    <Stack spacing={3} data-testid="env-diagnostics-page">
      <Box>
        <Typography variant="h2" data-testid="env-diagnostics-heading">
          Env diagnostics
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Required platform environment variables, their status, and (carefully) their values.
          Reveal calls are audit-logged.
        </Typography>
      </Box>

      {isError ? (
        <Alert severity="error" data-testid="env-diagnostics-error">
          {error?.message ?? "Could not load env diagnostics."}
        </Alert>
      ) : null}

      {!isError ? (
        <Alert severity={summary.severity} data-testid="env-summary-banner">
          {summary.message}
        </Alert>
      ) : null}

      {isPending ? (
        <Paper variant="outlined" sx={{ py: 6, display: "flex", justifyContent: "center" }}>
          <CircularProgress />
        </Paper>
      ) : rows && rows.length > 0 ? (
        <Paper variant="outlined">
          <TableContainer>
            <Table aria-label="Environment variables" data-testid="env-diagnostics-table">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: { xs: "30%", md: "20%" } }}>Name</TableCell>
                  <TableCell sx={{ width: "10%" }}>Status</TableCell>
                  <TableCell>Value</TableCell>
                  <TableCell sx={{ display: { xs: "none", md: "table-cell" } }}>
                    Description
                  </TableCell>
                  <TableCell align="right" sx={{ width: "12%" }}>
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {grouped.map((g) => (
                  <RowsForGroup
                    key={g.group}
                    group={g.group}
                    rows={g.rows}
                    revealed={revealed}
                    onReveal={handleReveal}
                    onCopy={handleCopy}
                    revealPending={reveal.isPending}
                  />
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      ) : null}

      <Snackbar
        open={toast.open}
        autoHideDuration={6000}
        onClose={() => setToast((t) => ({ ...t, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        data-testid="env-toast"
      >
        <Alert
          severity={toast.severity}
          onClose={() => setToast((t) => ({ ...t, open: false }))}
          sx={{ width: "100%" }}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </Stack>
  );
}

function RowsForGroup({
  group,
  rows,
  revealed,
  onReveal,
  onCopy,
  revealPending,
}: {
  group: EnvVarWire["group"];
  rows: EnvVarWire[];
  revealed: Record<string, RevealedEntry>;
  onReveal: (name: string) => void;
  onCopy: (name: string, value: string) => void;
  revealPending: boolean;
}): ReactElement {
  return (
    <>
      <TableRow data-testid={`env-group-header-${group}`}>
        <TableCell
          colSpan={5}
          sx={{
            backgroundColor: "background.default",
            borderBottom: 1,
            borderColor: "divider",
            py: 1.5,
          }}
        >
          <Typography variant="overline" sx={{ fontWeight: 600 }}>
            {GROUP_LABELS[group]}
          </Typography>
        </TableCell>
      </TableRow>
      {rows.map((row) => {
        const revealedEntry = revealed[row.name];
        const displayValue = revealedEntry ? revealedEntry.value : row.value;
        const canReveal = row.secret && row.status === "ok";
        return (
          <TableRow key={row.name} data-testid={`env-row-${row.name}`}>
            <TableCell>
              <Typography variant="body2" sx={{ fontFamily: "monospace", fontWeight: 500 }}>
                {row.name}
              </Typography>
              {row.required ? (
                <Typography variant="caption" color="text.secondary">
                  required
                </Typography>
              ) : null}
            </TableCell>
            <TableCell>
              <Chip
                label={statusLabelFor(row.status)}
                color={chipColorFor(row.status)}
                size="small"
                data-testid={`env-row-${row.name}-status-chip`}
              />
            </TableCell>
            <TableCell>
              <Typography
                variant="body2"
                sx={{
                  fontFamily: "monospace",
                  wordBreak: "break-all",
                  color: row.status === "missing" ? "text.disabled" : "text.primary",
                }}
                data-testid={`env-row-${row.name}-value`}
              >
                {displayValue || (row.status === "missing" ? "—" : "")}
              </Typography>
            </TableCell>
            <TableCell sx={{ display: { xs: "none", md: "table-cell" } }}>
              <Typography variant="body2" color="text.secondary">
                {row.description}
              </Typography>
            </TableCell>
            <TableCell align="right">
              <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                {canReveal ? (
                  <Tooltip
                    title={revealedEntry ? "Re-reveal (audit-logged)" : "Reveal (audit-logged)"}
                  >
                    <span>
                      <Button
                        size="small"
                        startIcon={<VisibilityIcon />}
                        onClick={() => onReveal(row.name)}
                        disabled={revealPending}
                        data-testid={`env-row-${row.name}-reveal`}
                      >
                        Reveal
                      </Button>
                    </span>
                  </Tooltip>
                ) : null}
                <Tooltip title="Copy current value to clipboard">
                  <span>
                    <IconButton
                      size="small"
                      onClick={() => onCopy(row.name, displayValue)}
                      disabled={!displayValue}
                      data-testid={`env-row-${row.name}-copy`}
                      aria-label={`Copy ${row.name}`}
                    >
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              </Stack>
            </TableCell>
          </TableRow>
        );
      })}
    </>
  );
}
